/**
 * Copy y datos de la landing. Los equipos, jugadores y cifras de los mockups son
 * inventados: pueblan las pantallas de ejemplo, no salen de ningún backend.
 *
 * La landing vende **profesionalización** —que una liga de barrio se vea y se corra como
 * una en serio—, no alivio operativo. Ese reencuadre y las decisiones que lo fijaron están
 * en FEATURE-177; el resumen vive en [marketing.md](../../../docs/modules/marketing.md).
 *
 * Voz: la del brand kit — frases cortas, en mayúsculas, afirmativas. Español de Chile.
 *
 * **Los precios son hipótesis sin validar** (CONTEXTO-PRODUCTO §8), igual que el plan de
 * entrada gratuito y el ciclo anual. No hay riel de cobro detrás: son copy.
 */

export const NAV = [
  { href: "#vitrina", label: "Vista pública" },
  { href: "#cancha", label: "En la cancha" },
  { href: "#como-funciona", label: "Cómo funciona" },
  { href: "#planes", label: "Planes" },
];

/** Las tres promesas del hero, bajo los botones. */
export const HERO_CHIPS = ["Sin instalar nada", "Tabla al instante", "Un link para toda la liga"];

// ── Tabla del hero ──────────────────────────────────────────────────────────
export interface Fila {
  pos: number;
  equipo: string;
  pj: number;
  pts: number;
  estado: "lider" | "normal" | "descenso";
  mov: "sube" | "baja" | "igual";
}

export const TABLA: Fila[] = [
  { pos: 1, equipo: "Atlético Sur", pj: 24, pts: 58, estado: "lider", mov: "igual" },
  { pos: 2, equipo: "Unión Norte", pj: 24, pts: 54, estado: "normal", mov: "sube" },
  { pos: 3, equipo: "Deportivo Río", pj: 24, pts: 51, estado: "normal", mov: "baja" },
  { pos: 4, equipo: "Central FC", pj: 24, pts: 49, estado: "normal", mov: "sube" },
  { pos: 17, equipo: "Estrella Vieja", pj: 24, pts: 21, estado: "descenso", mov: "baja" },
];

// ── Vista pública ───────────────────────────────────────────────────────────
export const VITRINA_CHECKS = [
  "Tu escudo y tu color en cada pantalla",
  "Tabla, fixture, goleadores y marcador en vivo",
  "Se abre en cualquier celular, sin instalar nada",
  "Se comparte por WhatsApp o por historia de Instagram",
];

/**
 * Lo que se ve en el celular del hincha. La liga es otra a propósito: el mockup va
 * pintado con SU color, no con el de La Tablapp, y eso es lo que demuestra que la vista
 * pública es white-label sin tener que escribirlo en un bullet.
 */
export const VITRINA_MOCK = {
  liga: "Liga de Quilpué",
  torneo: "Apertura 2026 · Serie A",
  vivo: { local: "Atlético Sur", visita: "Puerto FC", marcador: "2 – 1", minuto: "67'" },
};

// ── El delegado en la cancha ────────────────────────────────────────────────
export const CANCHA_CHECKS = [
  "Gol, tarjeta y cambio a un toque, con el cronómetro corriendo",
  "Se equivocó: lo corrige y la tabla se recalcula sola",
  "El hincha lo ve en el mismo minuto, sin que nadie publique nada",
  "Se cierra con la foto del acta física, firmada",
];

/** Botonera del acta que ve el delegado en su celular. */
export const CANCHA_ACCIONES = [
  { label: "Gol", tono: "accent" as const },
  { label: "Amarilla", tono: "gold" as const },
  { label: "Roja", tono: "loss" as const },
  { label: "Cambio", tono: "muted" as const },
];

/** La última jugada, para mostrar que se puede deshacer. */
export const CANCHA_ULTIMA = { min: "63'", texto: "Gol de M. Cortés (2)", equipo: "Atlético Sur" };

// ── Producto ────────────────────────────────────────────────────────────────
export const ACTA = {
  minuto: "67'",
  local: { nombre: "Atlético Sur", goles: 2 },
  visita: { nombre: "Puerto FC", goles: 1 },
  eventos: [
    { min: "12'", tipo: "gol" as const, texto: "Gol de M. Cortés" },
    { min: "34'", tipo: "amarilla" as const, texto: "Amarilla a J. Fuentes" },
    { min: "41'", tipo: "gol" as const, texto: "Gol de D. Rojas" },
    { min: "63'", tipo: "gol" as const, texto: "Gol de M. Cortés (2)" },
  ],
};

export const FIXTURE = [
  { hora: "10:00", local: "Unión Norte", visita: "Central FC", cancha: "Cancha 1", estado: "final" as const, marcador: "2 - 2" },
  { hora: "11:30", local: "Atlético Sur", visita: "Puerto FC", cancha: "Cancha 1", estado: "vivo" as const, marcador: "2 - 1" },
  { hora: "13:00", local: "Deportivo Río", visita: "Estrella Vieja", cancha: "Cancha 2", estado: "pendiente" as const, marcador: "—" },
];

export const GOLEADORES = [
  { pos: 1, nombre: "M. Cortés", equipo: "Atlético Sur", goles: 18 },
  { pos: 2, nombre: "D. Rojas", equipo: "Puerto FC", goles: 15 },
  { pos: 3, nombre: "I. Salinas", equipo: "Unión Norte", goles: 13 },
];

export const BLOQUES = [
  {
    id: "acta",
    etiqueta: "Tiempo real",
    tono: "accent2" as const,
    titulo: "El acta se llena en la cancha",
    texto:
      "El delegado anota goles, autogoles y tarjetas a un toque, con el cronómetro corriendo. Cada jugada aparece al instante para los que están mirando desde la casa.",
    checks: [
      "Cronómetro del partido a la vista de todos",
      "Cronología completa, jugada por jugada",
      "Se cierra con la foto del acta física",
    ],
  },
  {
    id: "fixture",
    etiqueta: "Fixture",
    tono: "accent" as const,
    titulo: "La fecha, ordenada por jornada",
    texto:
      "Cancha, hora y rival de cada partido, fecha por fecha. Las reprogramaciones y los W.O. no rompen nada: la tabla se recalcula sola.",
    checks: [
      "Navegación fecha por fecha",
      "Programa por cancha y por serie",
      "Estado claro: pendiente · en vivo · final",
    ],
  },
  {
    id: "stats",
    etiqueta: "Estadísticas",
    tono: "accent" as const,
    titulo: "El goleador deja de contarse a mano",
    texto:
      "El ranking de goleadores y las tarjetas salen del acta, no de la memoria de nadie. Las suspensiones por acumulación se avisan antes del partido.",
    checks: [
      "Goleadores por torneo y por categoría",
      "Tarjetas y suspensiones automáticas",
      "Todo público, para que nadie reclame",
    ],
  },
];

// ── Puesta en marcha ────────────────────────────────────────────────────────
export const PASOS = [
  { n: "01", titulo: "Crea la liga", texto: "Torneo, categorías y equipos en minutos. Puedes importar tus planillas de siempre." },
  { n: "02", titulo: "Carga el fixture", texto: "Genera las fechas y asigna cancha y horario a cada partido." },
  { n: "03", titulo: "Comparte el link", texto: "Tus jugadores e hinchas siguen la fecha en vivo, sin instalar nada." },
];

// ── Planes ──────────────────────────────────────────────────────────────────

export type Ciclo = "mensual" | "anual";

export interface PlanLanding {
  nombre: string;
  /** CLP/mes con IVA incluido, mes a mes. `0` = gratis. */
  mensual: number;
  /** CLP/mes con IVA incluido, pagando el año por adelantado. */
  anual: number;
  desc: string;
  destacado: boolean;
  features: string[];
  cta: string;
  /**
   * A dónde lleva el botón. `registro` es la puerta de autoservicio; `contacto` abre el
   * correo, y es lo que necesita una asociación que quiere hablar con alguien antes de
   * dejar su tarjeta. Es un dato del plan y no una constante compartida a propósito: los
   * tres CTA apuntaban al mismo destino, así que «Hablemos» llevaba a un formulario de
   * cuenta sin que nadie lo hubiera decidido.
   */
  destino: "registro" | "contacto";
}

export const PLANES: PlanLanding[] = [
  {
    nombre: "Prueba",
    mensual: 0,
    anual: 0,
    desc: "Para armar un torneo y ver si la app te sirve.",
    destacado: false,
    features: ["Un torneo a la vez, hasta 12 equipos", "Acta desde el celular", "Vista pública para tus hinchas"],
    cta: "Probar gratis",
    destino: "registro",
  },
  {
    nombre: "Liga",
    mensual: 14990,
    anual: 12492,
    desc: "Para el organizador que ya corre su liga.",
    destacado: true,
    features: [
      "Hasta 3 torneos en paralelo",
      "Tu marca: escudo y color de la liga",
      "Acta en vivo, sin app que instalar",
      "Goleadores, tarjetas y suspensiones al día",
    ],
    cta: "Comenzar",
    destino: "registro",
  },
  {
    nombre: "Asociación",
    mensual: 39990,
    anual: 33325,
    desc: "Para asociaciones con varias series y canchas.",
    destacado: false,
    features: [
      "Torneos y equipos sin tope",
      "Administradores por alcance",
      "Programa por cancha y por serie",
      "Reportes y exportes",
    ],
    cta: "Hablemos",
    destino: "contacto",
  },
];

export const PLANES_NOTA =
  "Precios en pesos chilenos, IVA incluido. Sin costo por equipo ni por jugador, y sin contrato: se paga la temporada que corres.";

/** `$14.990`. Sin decimales: en Chile el peso no los usa. */
export const clp = (n: number): string => `$${Math.round(n).toLocaleString("es-CL")}`;

// ── Contacto ────────────────────────────────────────────────────────────────

/**
 * El único canal que existe hoy. Sin plazo de respuesta, sin horario y sin formulario:
 * nada de eso está definido, y una landing que lo promete obliga a cumplirlo el lunes.
 */
export const CORREO_CONTACTO = "contacto@latablapp.cl";
